/**
 * task-type-automations-store — interface com /api/v1/task-type-automations
 * (R5.2 Bloco L).
 *
 * Sistema paralelo ao automations-store (que é por coluna de kanban). Aqui
 * automações são por activity_type_id + lifecycle trigger.
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type TaskTypeTrigger = 'on_create' | 'on_complete' | 'on_overdue' | 'on_assigned';
export type TaskTypeActionType = 'notify_in_app' | 'send_whatsapp' | 'send_email';

export interface TaskTypeAction {
  type: TaskTypeActionType;
  target: string;
  message?: string;
}

export interface TaskTypeAutomation {
  id: string;
  activityTypeId: string;
  nome: string;
  trigger: TaskTypeTrigger;
  actions: TaskTypeAction[];
  enabled: boolean;
  totalExecucoes: number;
  ultimaExecucaoIso: string | null;
  criadoPorId: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface CreateTaskTypeAutomationInput {
  activityTypeId: string;
  nome: string;
  trigger: TaskTypeTrigger;
  actions: TaskTypeAction[];
  enabled?: boolean;
}

export type UpdateTaskTypeAutomationInput = Partial<
  Omit<CreateTaskTypeAutomationInput, 'activityTypeId'>
>;

export interface TaskTypeAutomationRun {
  id: string;
  automationId: string;
  targetId: string;
  status: 'success' | 'partial' | 'error';
  actionResults: Array<{
    action: TaskTypeAction;
    status: 'success' | 'error';
    detail?: string;
  }>;
  timestampIso: string;
}

export const TTA_KEYS = {
  all: ['v1', 'task-type-automations'] as const,
  list: (filters: Record<string, unknown>) =>
    ['v1', 'task-type-automations', { filters }] as const,
  byId: (id: string) => ['v1', 'task-type-automations', id] as const,
  runs: (id: string) => ['v1', 'task-type-automations', id, 'runs'] as const,
};

export function useTaskTypeAutomations(activityTypeId: string | null = null) {
  const url = activityTypeId
    ? `/api/v1/task-type-automations?activity_type_id=${encodeURIComponent(activityTypeId)}`
    : '/api/v1/task-type-automations';
  return useQuery({
    queryKey: TTA_KEYS.list({ activityTypeId }),
    queryFn: () => api.get<TaskTypeAutomation[]>(url),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useTaskTypeAutomationRuns(automationId: string | null, limit = 25) {
  return useQuery({
    queryKey: TTA_KEYS.runs(automationId ?? ''),
    queryFn: () =>
      api.get<TaskTypeAutomationRun[]>(
        `/api/v1/task-type-automations/${encodeURIComponent(automationId!)}/runs?limit=${limit}`
      ),
    enabled: !!automationId && hasToken(),
    staleTime: 30_000,
  });
}

export function useTaskTypeAutomationActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: TTA_KEYS.all });

  const create = useCallback(
    async (input: CreateTaskTypeAutomationInput): Promise<TaskTypeAutomation> => {
      setPending('create');
      try {
        const a = await api.post<TaskTypeAutomation>('/api/v1/task-type-automations', input);
        invalidate();
        return a;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const update = useCallback(
    async (id: string, input: UpdateTaskTypeAutomationInput): Promise<TaskTypeAutomation> => {
      setPending(id);
      try {
        const a = await api.put<TaskTypeAutomation>(
          `/api/v1/task-type-automations/${encodeURIComponent(id)}`,
          input
        );
        invalidate();
        return a;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      setPending(id);
      try {
        await api.delete(`/api/v1/task-type-automations/${encodeURIComponent(id)}`);
        invalidate();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const dispatch = useCallback(
    async (
      id: string,
      input: { targetId: string; responsavelId?: string; criadoPorId?: string }
    ): Promise<{ runsCreated: number }> => {
      setPending(`dispatch-${id}`);
      try {
        const r = await api.post<{ ok: true; runsCreated: number }>(
          `/api/v1/task-type-automations/${encodeURIComponent(id)}/dispatch`,
          input
        );
        qc.invalidateQueries({ queryKey: TTA_KEYS.runs(id) });
        return { runsCreated: r.runsCreated };
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return { create, update, remove, dispatch, pending };
}
